import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { buttonClasses } from '@/design-system/Button'
import { Container, Skeleton } from '@/design-system/primitives'
import { PublicLayout } from '@/layouts/PublicLayout'
import { cn } from '@/lib/cn'

import { useEventContent, usePublicEvent } from '../api'

/**
 * Les documents légaux : conditions, confidentialité, mentions, règlement.
 *
 * Ils ne sont pas écrits dans le code mais stockés comme blocs de contenu,
 * avec une clé réservée. L'équipe les corrige depuis le back-office quand une
 * pratique change ou qu'un prestataire est remplacé — sans développeur et sans
 * déploiement, ce qui est la seule façon qu'ils restent exacts.
 *
 * Le rendu est volontairement sobre : une colonne étroite, un interlignage
 * généreux, pas de décor. Ce sont des textes qu'on lit d'un bout à l'autre, et
 * la seule chose qui compte est qu'ils soient lisibles.
 */
export function LegalPage({ blockKey }: { blockKey: string }) {
  const { t } = useTranslation()
  const eventQuery = usePublicEvent()
  const content = useEventContent(eventQuery.data?.slug)

  const block = (content.data ?? []).find((row) => row.key === blockKey)

  useEffect(() => {
    if (block) document.title = block.title
  }, [block])

  if (content.isPending) {
    return (
      <PublicLayout>
        <Container className="max-w-3xl py-16">
          <Skeleton className="h-10 w-72" />
          <Skeleton className="mt-8 h-96 w-full" />
        </Container>
      </PublicLayout>
    )
  }

  if (!block) {
    return (
      <PublicLayout>
        <Container className="max-w-3xl py-16">
          <h1 className="font-display text-display-sm text-content-primary">
            {t('legal.missingTitle')}
          </h1>
          <p className="mt-4 text-content-secondary">{t('legal.missingBody')}</p>
          {blockKey === PRIVACY_KEY ? <MyDataLink /> : null}
        </Container>
      </PublicLayout>
    )
  }

  return (
    <PublicLayout>
      <Container className="max-w-3xl py-16">
        <h1 className="font-display text-display-sm text-content-primary">{block.title}</h1>
        {block.subtitle ? (
          <p className="mt-3 text-content-secondary">{block.subtitle}</p>
        ) : null}

        <LegalBody body={block.body} className="mt-10" />
        {blockKey === PRIVACY_KEY ? <MyDataLink /> : null}
      </Container>
    </PublicLayout>
  )
}

/** La politique de confidentialité, seule page légale qui ouvre sur « Mes données ». */
const PRIVACY_KEY = 'legal.privacy'

/**
 * Le passage du texte au geste.
 *
 * La politique énumère des droits ; sans un bouton pour les exercer, le
 * lecteur doit deviner à qui écrire et quoi joindre. Le lien vit dans le code
 * et non dans le texte éditable : une correction du document ne doit pas
 * pouvoir le faire disparaître.
 */
function MyDataLink() {
  const { t } = useTranslation()
  return (
    <div className="mt-12 rounded-lg border border-line-subtle bg-bg-elevated p-6">
      <p className="text-content-primary">{t('myData.privacyCallout')}</p>
      <Link to="/mes-donnees" className={buttonClasses({ variant: 'outline', className: 'mt-4' })}>
        {t('myData.privacyCta')}
      </Link>
    </div>
  )
}

/**
 * Le corps du document.
 *
 * Un sous-ensemble de Markdown, rendu à la main : titres en gras, italiques,
 * paragraphes séparés par une ligne vide. Pas de bibliothèque et surtout
 * **pas de `dangerouslySetInnerHTML`** : ces textes sont éditables depuis le
 * back-office, et un jour quelqu'un y collera du HTML récupéré ailleurs. Le
 * rendre tel quel ouvrirait une injection sur les pages les plus sensibles du
 * site.
 */
export function LegalBody({ body, className }: { body: string; className?: string }) {
  const paragraphs = body.split(/\n{2,}/).filter((part) => part.trim())

  return (
    <div className={cn('space-y-5 text-content-secondary', className)}>
      {paragraphs.map((paragraph, index) => {
        const trimmed = paragraph.trim()

        // Un paragraphe entièrement en gras est un titre de section : c'est la
        // convention qu'emploient les textes, et la traiter ainsi évite de
        // demander à l'organisateur d'apprendre une syntaxe de plus.
        const heading = /^\*\*(.+)\*\*$/s.exec(trimmed)
        if (heading) {
          return (
            <h2 key={index} className="pt-4 font-display text-heading-md text-content-primary">
              {heading[1]}
            </h2>
          )
        }

        return (
          <p key={index} className="whitespace-pre-line leading-relaxed">
            {inline(trimmed)}
          </p>
        )
      })}
    </div>
  )
}

/** Gras et italique, sans jamais interpréter de balise. */
function inline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g)

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="text-content-primary">
          {part.slice(2, -2)}
        </strong>
      )
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={index}>{part.slice(1, -1)}</em>
    }
    return <span key={index}>{part}</span>
  })
}
